<template>
  <div class="dossier-sops">
    <div class="dossier-sops__wrapper">
      <!-- ─── Cabeçalho ─── -->
      <div class="dossier-sops__header" @click="toggleCollapse">
        <div class="dossier-sops__header-left">
          <span class="dossier-sops__state-icon" :class="headerStateClass">
            <i :class="headerStateIcon"></i>
          </span>
          <div>
            <h3 class="dossier-sops__title">
              POPs – Procedimentos Operacionais Padrão
            </h3>
            <span v-if="isCollapsed && false" class="dossier-sops__subtitle">
              {{ progressSummary }}
            </span>
          </div>
        </div>
        <button
          class="dossier-sops__toggle"
          type="button"
          :aria-expanded="!isCollapsed"
        >
          <i
            class="fas"
            :class="isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"
          ></i>
        </button>
      </div>

      <!-- ─── Corpo ─── -->
      <div v-show="!isCollapsed" class="dossier-sops__body">
        <!-- Explicativo -->
        <div v-if="!isViewMode" class="dossier-sops__explainer">
          <p>
            Registre os Procedimentos Operacionais Padrão da ILPI. O sistema
            apresenta a lista padrão para preenchimento progressivo — sem travar
            nem exigir todos de uma vez. Marque "Não necessário" nos POPs que
            não se aplicam à estrutura da instituição.<br />
            O Sistema Clin não valida o conteúdo dos POPs, apenas organiza os
            anexos, controla validade e revisão para gestão interna e
            apresentação em fiscalizações.
          </p>
        </div>

        <!-- Pergunta inicial -->
        <div class="dossier-conditional__question">
          <label class="dossier-conditional__question-label">
            <i class="fas fa-question-circle mr-1"></i>
            A ILPI possui POPs formalizados?
          </label>
          <div class="dossier-conditional__options">
            <label
              v-for="opt in initialStatusOptions"
              :key="opt.value"
              class="dossier-conditional__option"
            >
              <input
                type="radio"
                :value="opt.value"
                v-model="formData.initial_status"
                :disabled="isViewMode"
                class="dossier-conditional__radio"
              />
              <span>{{ opt.label }}</span>
            </label>
          </div>
        </div>

        <!-- Filtros / busca -->
        <div v-if="formData.initial_status" class="dossier-sops__toolbar">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Buscar POP"
            dense
            outlined
            hide-details
            clearable
            class="dossier-sops__search"
          />

          <v-select
            v-model="filterStatus"
            :items="filterOptions"
            dense
            outlined
            hide-details
            clearable
            placeholder="Todos os status"
            class="dossier-sops__filter"
          />

          <!-- Progresso geral -->
          <div class="dossier-sops__progress-summary" v-if="false">
            <span class="dossier-sops__progress-text">{{
              progressSummary
            }}</span>
            <div class="dossier-sops__progress-bar">
              <div
                class="dossier-sops__progress-fill"
                :style="{ width: progressPct + '%' }"
              ></div>
            </div>
          </div>
        </div>

        <!-- Lista de POPs (padrão + outros) -->
        <template v-if="formData.initial_status">
          <div
            v-for="sop in filteredSops"
            :key="sop.key"
            class="dossier-sops__item"
            :class="sopItemClass(sop.key)"
            v-show="mode == 'view' ? getSopData(sop.key).file_url : true"
          >
            <!-- Cabeçalho do POP -->
            <div class="dossier-sops__item-header" @click="toggleSop(sop.key)">
              <span
                class="dossier-sops__item-state"
                :class="sopStateClass(sop.key)"
              >
                <i :class="sopStateIcon(sop.key)"></i>
              </span>
              <span class="dossier-sops__item-name">{{ sop.label }}</span>
              <span
                v-if="getSopData(sop.key).status"
                class="dossier-sops__item-status-badge"
                :class="sopBadgeClass(sop.key)"
              >
                {{ getSopData(sop.key).status }}
              </span>
              <span
                v-if="getSopData(sop.key).validity_date"
                class="dossier-sops__item-validity"
              >
                <i class="fas fa-calendar-alt mr-1"></i>
                {{ formatDateBR(getSopData(sop.key).validity_date) }}
              </span>
              <i
                class="fas dossier-sops__item-chevron"
                :class="
                  expandedSops.includes(sop.key)
                    ? 'fa-chevron-up'
                    : 'fa-chevron-down'
                "
              ></i>
            </div>

            <!-- Formulário do POP (expandido) -->
            <div
              v-if="expandedSops.includes(sop.key)"
              class="dossier-sops__item-body"
            >
              <!-- Status -->
              <div class="dossier-sops__field">
                <label class="dossier-sops__label">Situação</label>
                <v-select
                  v-if="!isViewMode"
                  v-model="getSopData(sop.key).status"
                  :items="sopStatusOptions"
                  dense
                  outlined
                  hide-details
                  class="dossier-sops__input"
                />
                <span v-else class="dossier-sops__view-value">{{
                  getSopData(sop.key).status || "—"
                }}</span>
              </div>

              <template
                v-if="
                  getSopData(sop.key).status &&
                  getSopData(sop.key).status !== 'Não necessário'
                "
              >
                <!-- Validade -->
                <div class="dossier-sops__field">
                  <label class="dossier-sops__label">Validade</label>
                  <v-text-field
                    v-if="!isViewMode"
                    v-model="getSopData(sop.key).validity_date"
                    type="date"
                    dense
                    outlined
                    hide-details
                    class="dossier-sops__input dossier-sops__input--date"
                  />
                  <span v-else class="dossier-sops__view-value">
                    {{ formatDateBR(getSopData(sop.key).validity_date) }}
                  </span>
                </div>

                <!-- Última revisão -->
                <div class="dossier-sops__field">
                  <label class="dossier-sops__label"
                    >Data da última revisão</label
                  >
                  <v-text-field
                    v-if="!isViewMode"
                    v-model="getSopData(sop.key).last_review_date"
                    type="date"
                    dense
                    outlined
                    hide-details
                    class="dossier-sops__input dossier-sops__input--date"
                  />
                  <span v-else class="dossier-sops__view-value">
                    {{ formatDateBR(getSopData(sop.key).last_review_date) }}
                  </span>
                </div>

                <!-- Responsável pela revisão -->
                <div class="dossier-sops__field">
                  <label class="dossier-sops__label"
                    >Responsável pela revisão</label
                  >
                  <v-text-field
                    v-if="!isViewMode"
                    v-model="getSopData(sop.key).reviewer"
                    dense
                    outlined
                    hide-details
                    placeholder="Nome do responsável"
                    class="dossier-sops__input"
                  />
                  <span v-else class="dossier-sops__view-value">{{
                    getSopData(sop.key).reviewer || "—"
                  }}</span>
                </div>

                <!-- Upload do POP -->
                <div class="dossier-sops__field">
                  <label class="dossier-sops__label"
                    >Arquivo do POP (PDF)</label
                  >
                  <div
                    v-if="getSopData(sop.key).file_url"
                    class="dossier-sops__file-attached"
                  >
                    <i class="fas fa-file-pdf dossier-sops__file-icon"></i>
                    <span>{{
                      getSopData(sop.key).file_name || "POP anexado"
                    }}</span>
                    <a
                      :href="getSopData(sop.key).file_url"
                      target="_blank"
                      class="dossier-sops__file-link ml-2"
                    >
                      <i class="fas fa-external-link-alt mr-1"></i>Ver
                    </a>
                    <v-btn
                      v-if="!isViewMode"
                      x-small
                      text
                      class="btn-notcertified ml-1"
                      @click="triggerSopUpload(sop.key)"
                    >
                      <i class="fas fa-sync-alt mr-1"></i>Substituir
                    </v-btn>
                  </div>
                  <div v-else>
                    <v-btn
                      v-if="!isViewMode"
                      small
                      class="btn-add"
                      :loading="getSopData(sop.key)._uploading"
                      @click="triggerSopUpload(sop.key)"
                    >
                      <i class="fas fa-upload mr-1"></i>Anexar POP
                    </v-btn>
                    <span v-else class="dossier-sops__view-empty"
                      >Não anexado</span
                    >
                  </div>
                  <input
                    :ref="`sopUpload_${sop.key}`"
                    type="file"
                    accept="application/pdf"
                    style="display: none"
                    @change="(e) => handleSopUpload(e, sop.key)"
                  />
                </div>

                <!-- Observações -->
                <div class="dossier-sops__field">
                  <label class="dossier-sops__label">Observações</label>
                  <v-textarea
                    v-if="!isViewMode"
                    v-model="getSopData(sop.key).observations"
                    dense
                    outlined
                    rows="2"
                    auto-grow
                    hide-details
                    placeholder="Informações complementares (opcional)"
                  />
                  <p
                    v-else-if="getSopData(sop.key).observations"
                    class="dossier-sops__view-value"
                  >
                    {{ getSopData(sop.key).observations }}
                  </p>
                  <span v-else class="dossier-sops__view-empty">—</span>
                </div>
              </template>

              <!-- Salvar POP individual -->
              <div v-if="!isViewMode" class="dossier-sops__item-save">
                <v-btn
                  small
                  class="btn-add"
                  :loading="savingKeys.includes(sop.key)"
                  @click="saveSop(sop.key)"
                >
                  <i class="fas fa-save mr-1"></i>Salvar
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Outros POPs (campo livre) -->
          <div class="dossier-sops__others">
            <div class="dossier-sops__others-title">
              <i class="fas fa-plus-circle mr-1"></i>
              Outros POPs
            </div>

            <div
              v-for="(other, idx) in formData.other_sops"
              :key="`other_${idx}`"
              class="dossier-sops__other-item"
            >
              <div class="dossier-sops__other-header">
                <v-text-field
                  v-if="!isViewMode"
                  v-model="other.name"
                  dense
                  outlined
                  hide-details
                  placeholder="Nome do POP"
                  class="dossier-sops__other-name-input"
                />
                <span v-else class="dossier-sops__item-name">{{
                  other.name || "POP sem nome"
                }}</span>
                <v-btn
                  v-if="!isViewMode"
                  icon
                  small
                  class="btn-notcertified"
                  @click="formData.other_sops.splice(idx, 1)"
                >
                  <i class="fas fa-trash-alt"></i>
                </v-btn>
              </div>

              <div v-if="other.name" class="dossier-sops__other-fields">
                <v-select
                  v-if="!isViewMode"
                  v-model="other.status"
                  :items="sopStatusOptions"
                  dense
                  outlined
                  hide-details
                  placeholder="Situação"
                  class="dossier-sops__input mb-2"
                />
                <v-text-field
                  v-if="
                    !isViewMode &&
                    other.status &&
                    other.status !== 'Não necessário'
                  "
                  v-model="other.validity_date"
                  type="date"
                  dense
                  outlined
                  hide-details
                  class="dossier-sops__input dossier-sops__input--date mb-2"
                />
                <div v-if="other.file_url" class="dossier-sops__file-attached">
                  <i class="fas fa-file-pdf dossier-sops__file-icon"></i>
                  <a
                    :href="other.file_url"
                    target="_blank"
                    class="dossier-sops__file-link"
                    >Ver PDF</a
                  >
                </div>
                <v-btn
                  v-else-if="!isViewMode"
                  small
                  class="btn-add"
                  :loading="other._uploading"
                  @click="triggerOtherUpload(idx)"
                >
                  <i class="fas fa-upload mr-1"></i>Anexar POP
                </v-btn>
                <input
                  :ref="`otherUpload_${idx}`"
                  type="file"
                  accept="application/pdf"
                  style="display: none"
                  @change="(e) => handleOtherUpload(e, idx)"
                />
              </div>
            </div>

            <v-btn
              v-if="!isViewMode"
              small
              class="btn-add mt-2"
              @click="addOtherSop"
            >
              <i class="fas fa-plus mr-1"></i>Adicionar outro POP
            </v-btn>
          </div>

          <!-- Salvar todos os metadados -->
          <div v-if="!isViewMode" class="dossier-sops__save">
            <v-btn class="btn-add" :loading="saveLoading" @click="saveMetadata">
              <i class="fas fa-save mr-1"></i>Salvar configuração dos POPs
            </v-btn>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import toast from "../../lib/ToastNotification";

// ─── Lista padrão de POPs ─────────────────────────────────────────────────────
const STANDARD_SOPS = [
  { key: "limpeza_concorrente", label: "POP de limpeza concorrente" },
  { key: "limpeza_terminal", label: "POP de limpeza terminal" },
  {
    key: "limpeza_superficies",
    label: "POP de limpeza e desinfecção de superfícies",
  },
  { key: "higienizacao_maos", label: "POP de higienização das mãos" },
  {
    key: "lavanderia_interna",
    label: "POP de lavanderia ou gerenciamento de roupas (lavanderia interna)",
  },
  {
    key: "enxoval_terceirizado",
    label: "POP de recebimento de enxoval terceirizado",
  },
  {
    key: "manipulacao_alimentos",
    label: "POP de manipulação de alimentos (cozinha própria)",
  },
  { key: "alimentacao_terceirizada", label: "POP de alimentação terceirizada" },
  {
    key: "controle_temperaturas",
    label: "POP de controle de temperaturas e armazenamento de alimentos",
  },
  {
    key: "administracao_medicamentos",
    label: "POP de administração de medicamentos",
  },
  {
    key: "armazenamento_medicamentos",
    label: "POP de armazenamento e controle de validade de medicamentos",
  },
  {
    key: "prevencao_quedas",
    label: "POP de prevenção de quedas e conduta após queda",
  },
  {
    key: "lesao_pressao",
    label: "POP de prevenção e cuidados com lesão por pressão",
  },
  {
    key: "emergencia_remocao",
    label: "POP de emergência e remoção para serviço de saúde",
  },
  {
    key: "intercorrencias_clinicas",
    label: "POP de manejo de intercorrências clínicas",
  },
  { key: "admissao_residente", label: "POP de admissão do residente" },
  { key: "visitas_saidas", label: "POP de visitas e saídas" },
  { key: "obito_comunicacao", label: "POP de óbito e comunicação interna" },
  { key: "gerenciamento_residuos", label: "POP de gerenciamento de resíduos" },
  {
    key: "controle_pragas_interface",
    label: "POP de controle de pragas – interface com prestador",
  },
];

const emptySopData = () => ({
  status: null,
  validity_date: null,
  last_review_date: null,
  reviewer: "",
  file_url: null,
  file_name: null,
  observations: "",
  _uploading: false,
});

export default {
  name: "DossierSOPs",
  props: {
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },
    initialData: { type: Object, default: () => ({}) },
    startCollapsed: { type: Boolean, default: false },
  },

  data() {
    const d = this.initialData || {};
    return {
      isCollapsed: this.startCollapsed,
      saveLoading: false,
      savingKeys: [],

      search: "",
      filterStatus: null,
      expandedSops: [],

      standardSops: STANDARD_SOPS,

      initialStatusOptions: [
        { value: "possui", label: "Sim, possui POPs formalizados" },
        { value: "em_implantacao", label: "Em implantação" },
        { value: "nao_possui", label: "Não possui" },
      ],

      sopStatusOptions: [
        "Possui vigente",
        "Possui provisório",
        "Em elaboração",
        "Em revisão",
        "Não possui (pendente)",
        "Não necessário",
      ],

      filterOptions: [
        "Possui vigente",
        "Possui provisório",
        "Em elaboração",
        "Em revisão",
        "Não possui (pendente)",
        "Não necessário",
        "Não informado",
      ],

      formData: {
        initial_status: d.initial_status || null,
        sops_data: d.sops_data || {},
        other_sops: d.other_sops || [],
      },
    };
  },

  watch: {
    initialData() {
      let { initialData: d } = this;
      this.formData = {
        initial_status: d.initial_status || null,
        sops_data: d.sops_data || {},
        other_sops: d.other_sops || [],
      };
    },
  },

  computed: {
    isViewMode() {
      return this.mode === "view";
    },

    filteredSops() {
      let list = STANDARD_SOPS;
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter((s) => s.label.toLowerCase().includes(q));
      }
      if (this.filterStatus) {
        list = list.filter((s) => {
          const status = this.getSopData(s.key).status;
          if (this.filterStatus === "Não informado") return !status;
          return status === this.filterStatus;
        });
      }
      return list;
    },

    // Progresso: SOPs com status preenchido / total
    filledCount() {
      return STANDARD_SOPS.filter((s) => !!this.getSopData(s.key).status)
        .length;
    },

    progressPct() {
      return Math.round((this.filledCount / STANDARD_SOPS.length) * 100);
    },

    progressSummary() {
      return `${this.filledCount}/${STANDARD_SOPS.length} POPs preenchidos`;
    },

    headerState() {
      if (!this.formData.initial_status) return "pending";
      if (this.progressPct === 100) return "ok";
      if (this.progressPct > 0) return "partial";
      return "pending";
    },

    headerStateClass() {
      const map = { ok: "--ok", partial: "--partial", pending: "--pending" };
      return `dossier-sops__state-icon${map[this.headerState] || "--pending"}`;
    },

    headerStateIcon() {
      return (
        {
          ok: "fas fa-check-circle",
          partial: "fas fa-adjust",
          pending: "far fa-circle",
        }[this.headerState] || "far fa-circle"
      );
    },
  },

  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },

    toggleSop(key) {
      const idx = this.expandedSops.indexOf(key);
      if (idx === -1) this.expandedSops.push(key);
      else this.expandedSops.splice(idx, 1);
    },

    // Acesso reativo aos dados de cada POP
    getSopData(key) {
      if (!this.formData.sops_data[key]) {
        this.$set(this.formData.sops_data, key, emptySopData());
      }
      return this.formData.sops_data[key];
    },

    // ── Estado visual por POP ──────────────────────────────────────────────
    sopItemClass(key) {
      const s = this.getSopData(key).status;
      if (!s) return "";
      if (s === "Não necessário") return "dossier-sops__item--na";
      if (s === "Possui vigente") return "dossier-sops__item--ok";
      if (s === "Não possui (pendente)") return "dossier-sops__item--pending";
      return "dossier-sops__item--partial";
    },

    sopStateClass(key) {
      const map = {
        "Possui vigente": "--ok",
        "Possui provisório": "--partial",
        "Em elaboração": "--partial",
        "Em revisão": "--partial",
        "Não possui (pendente)": "--pending",
        "Não necessário": "--na",
      };
      const s = this.getSopData(key).status;
      return `dossier-sops__item-state${map[s] || "--pending"}`;
    },

    sopStateIcon(key) {
      const s = this.getSopData(key).status;
      if (!s) return "far fa-circle";
      if (s === "Possui vigente") return "fas fa-check-circle";
      if (s === "Não necessário") return "fas fa-minus-circle";
      if (s === "Não possui (pendente)") return "far fa-circle";
      return "fas fa-adjust";
    },

    sopBadgeClass(key) {
      const s = this.getSopData(key).status;
      const map = {
        "Possui vigente": "dossier-sops__badge--ok",
        "Possui provisório": "dossier-sops__badge--partial",
        "Em elaboração": "dossier-sops__badge--partial",
        "Em revisão": "dossier-sops__badge--partial",
        "Não possui (pendente)": "dossier-sops__badge--pending",
        "Não necessário": "dossier-sops__badge--na",
      };
      return map[s] || "";
    },

    // ── Upload por POP ─────────────────────────────────────────────────────
    triggerSopUpload(key) {
      const ref = this.$refs[`sopUpload_${key}`];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el) el.click();
    },

    async handleSopUpload(event, key) {
      const file = event.target.files[0];
      if (!file) return;
      this.$set(this.formData.sops_data[key], "_uploading", true);
      try {
        const fd = new FormData();
        fd.append("file", file, file.name);
        fd.append("sop_key", key);
        const response = await api.post("/rest-home/dossier/sops/upload", fd);
        const { error, file_url, file_name, message } = response.data;
        if (!error) {
          this.$set(this.formData.sops_data[key], "file_url", file_url);
          this.$set(
            this.formData.sops_data[key],
            "file_name",
            file_name || file.name
          );
          toast(message || "POP anexado com sucesso", "success");
        }
      } catch (err) {
        console.error("DossierSOPs.upload:", err);
      } finally {
        this.$set(this.formData.sops_data[key], "_uploading", false);
        event.target.value = "";
      }
    },

    // ── Upload para outros POPs ────────────────────────────────────────────
    triggerOtherUpload(idx) {
      const ref = this.$refs[`otherUpload_${idx}`];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el) el.click();
    },

    async handleOtherUpload(event, idx) {
      const file = event.target.files[0];
      if (!file) return;
      this.$set(this.formData.other_sops[idx], "_uploading", true);
      try {
        const fd = new FormData();
        fd.append("file", file, file.name);
        fd.append("sop_index", idx);
        const response = await api.post(
          "/rest-home/dossier/sops/other/upload",
          fd
        );
        const { error, file_url, file_name } = response.data;
        if (!error) {
          this.$set(this.formData.other_sops[idx], "file_url", file_url);
          this.$set(
            this.formData.other_sops[idx],
            "file_name",
            file_name || file.name
          );
          toast("POP anexado com sucesso", "success");
        }
      } catch (err) {
        console.error("DossierSOPs.otherUpload:", err);
      } finally {
        this.$set(this.formData.other_sops[idx], "_uploading", false);
        event.target.value = "";
      }
    },

    addOtherSop() {
      this.formData.other_sops.push({
        name: "",
        status: null,
        validity_date: null,
        file_url: null,
        file_name: null,
        _uploading: false,
      });
    },

    // ── Salvar POP individual ──────────────────────────────────────────────
    async saveSop(key) {
      try {
        this.savingKeys.push(key);
        const data = { ...this.formData.sops_data[key] };
        delete data._uploading;

        const response = await api.post("/rest-home/dossier/sops/item", {
          sop_key: key,
          ...data,
        });
        const { error, message } = response.data;
        if (!error) {
          toast(message || "POP salvo com sucesso", "success");
          this.$emit("saved", { module: "sops", sop_key: key });
        }
      } catch (err) {
        console.error("DossierSOPs.saveSop:", err);
      } finally {
        this.savingKeys = this.savingKeys.filter((k) => k !== key);
      }
    },

    // ── Salvar metadados (status inicial + outros) ─────────────────────────
    async saveMetadata() {
      try {
        this.saveLoading = true;
        const payload = {
          initial_status: this.formData.initial_status,
          other_sops: this.formData.other_sops.map((s) => {
            const clean = { ...s };
            delete clean._uploading;
            return clean;
          }),
        };
        const response = await api.post(
          "/rest-home/dossier/sops/metadata",
          payload
        );
        const { error, message } = response.data;
        if (!error) {
          toast(message || "Configuração salva com sucesso", "success");
          this.$emit("saved", { module: "sops", metadata: true });
        }
      } catch (err) {
        console.error("DossierSOPs.saveMetadata:", err);
      } finally {
        this.saveLoading = false;
      }
    },

    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER POPs
   ═══════════════════════════════════════════════════════════════ */

.dossier-sops__wrapper {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
  transition: box-shadow 0.2s;
}
.dossier-sops__wrapper:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.dossier-sops__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f8f7;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  user-select: none;
}
.dossier-sops__header:hover {
  background: #f0f0ef;
}
.dossier-sops__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dossier-sops__title {
  font-size: 14px;
  font-weight: 600;
  color: #33495e;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.dossier-sops__subtitle {
  font-size: 12px;
  color: #888;
  display: block;
  margin-top: 2px;
}

.dossier-sops__state-icon {
  font-size: 15px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}
.dossier-sops__state-icon--ok {
  color: #30915b;
}
.dossier-sops__state-icon--partial {
  color: #fb8b01;
}
.dossier-sops__state-icon--pending {
  color: #ccc;
}

.dossier-sops__toggle {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #888;
  font-size: 13px;
}
.dossier-sops__toggle:hover {
  color: #1f5a76;
}

.dossier-sops__body {
  padding: 16px;
}

.dossier-sops__explainer {
  font-size: 13px;
  color: #555;
  background: #f8f8f7;
  border-left: 3px solid #1f5a76;
  border-radius: 0 4px 4px 0;
  padding: 10px 12px;
  margin-bottom: 14px;
  line-height: 1.6;
}
.dossier-sops__explainer p {
  margin: 0;
}

/* ── Toolbar ─────────────────────────────────────────────────── */
.dossier-sops__toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin: 12px 0;
}
.dossier-sops__search {
  flex: 1;
  min-width: 180px;
}
.dossier-sops__filter {
  flex: 0 1 200px;
}

.dossier-sops__progress-summary {
  flex: 0 0 160px;
}
.dossier-sops__progress-text {
  font-size: 11px;
  color: #888;
  display: block;
  margin-bottom: 3px;
}
.dossier-sops__progress-bar {
  height: 4px;
  background: #eee;
  border-radius: 2px;
  overflow: hidden;
}
.dossier-sops__progress-fill {
  height: 100%;
  background: #1f5a76;
  border-radius: 2px;
  transition: width 0.3s;
}

/* ── Item de POP ─────────────────────────────────────────────── */
.dossier-sops__item {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  margin-bottom: 4px;
  overflow: hidden;
}
.dossier-sops__item--ok {
  border-left: 3px solid #30915b;
}
.dossier-sops__item--partial {
  border-left: 3px solid #fb8b01;
}
.dossier-sops__item--pending {
  border-left: 3px solid #e0e0e0;
}
.dossier-sops__item--na {
  border-left: 3px solid #ddd;
  opacity: 0.7;
}

.dossier-sops__item-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  cursor: pointer;
  background: #fff;
  transition: background 0.15s;
}
.dossier-sops__item-header:hover {
  background: #fafafa;
}

/* ── Estado do item ──────────────────────────────────────────── */
.dossier-sops__item-state {
  font-size: 13px;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}
.dossier-sops__item-state--ok {
  color: #30915b;
}
.dossier-sops__item-state--partial {
  color: #fb8b01;
}
.dossier-sops__item-state--pending {
  color: #ccc;
}
.dossier-sops__item-state--na {
  color: #bbb;
}

.dossier-sops__item-name {
  font-size: 13px;
  color: #333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Badge de status ─────────────────────────────────────────── */
.dossier-sops__item-status-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  flex-shrink: 0;
}
.dossier-sops__badge--ok {
  background: #d4edda;
  color: #155724;
}
.dossier-sops__badge--partial {
  background: #fff3cd;
  color: #856404;
}
.dossier-sops__badge--pending {
  background: #f0f0f0;
  color: #888;
}
.dossier-sops__badge--na {
  background: #f0f0f0;
  color: #aaa;
}

.dossier-sops__item-validity {
  font-size: 11px;
  color: #888;
  flex-shrink: 0;
}

.dossier-sops__item-chevron {
  font-size: 11px;
  color: #ccc;
  flex-shrink: 0;
}

/* ── Corpo do POP expandido ──────────────────────────────────── */
.dossier-sops__item-body {
  padding: 12px;
  background: #fafafa;
  border-top: 1px solid #e8e8e8;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
}

.dossier-sops__field {
  display: flex;
  flex-direction: column;
}

.dossier-sops__label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 4px;
}

.dossier-sops__input {
  font-size: 12px;
}
.dossier-sops__input--date {
  max-width: 180px;
}

.dossier-sops__view-value {
  font-size: 12px;
  color: #333;
  padding: 3px 0;
  display: block;
}
.dossier-sops__view-empty {
  font-size: 11px;
  color: #aaa;
  font-style: italic;
}

/* ── Arquivo do POP ──────────────────────────────────────────── */
.dossier-sops__file-attached {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: #f4fff8;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  font-size: 12px;
}
.dossier-sops__file-icon {
  font-size: 14px;
  color: #dc3545;
}
.dossier-sops__file-link {
  font-size: 12px;
  color: #1f5a76;
  font-weight: 600;
}

/* ── Salvar POP ──────────────────────────────────────────────── */
.dossier-sops__item-save {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
  border-top: 1px solid #ebebeb;
}

/* ── Outros POPs ─────────────────────────────────────────────── */
.dossier-sops__others {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e8e8e8;
}

.dossier-sops__others-title {
  font-size: 12px;
  font-weight: 600;
  color: #1f5a76;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 10px;
}

.dossier-sops__other-item {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 10px;
  margin-bottom: 8px;
}

.dossier-sops__other-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.dossier-sops__other-name-input {
  flex: 1;
  font-size: 13px;
}

.dossier-sops__other-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
}

/* ── Salvar metadados ────────────────────────────────────────── */
.dossier-sops__save {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0ef;
}

/* ── Responsivo ──────────────────────────────────────────────── */
@media (max-width: 600px) {
  .dossier-sops__body {
    padding: 12px;
  }
  .dossier-sops__toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  .dossier-sops__search,
  .dossier-sops__filter {
    flex: unset;
    width: 100%;
  }
  .dossier-sops__item-body {
    grid-template-columns: 1fr;
  }
  .dossier-sops__item-validity {
    display: none;
  }
}
</style>